import { alpha, createTheme } from '@mui/material/styles'
import { COLOR, KEY_VALUES, LAYOUT } from "../modules/constants"

const buttonCommon = {
  fontSize: "14px",
  fontWeight: 400,
  textTransform: "none",
  border: "none",
  outline: "none",
  lineHeight: 1.1,
}

export const theme = createTheme({
  palette: {
    primary: {
      // main: '#3f51b5', // v4 primary
      main: COLOR.BRAND.STD,
      contrastText: COLOR.WHITE,
    },
    secondary: {
      main: COLOR.GOOD,
      // main: '#f50057', // v4 secondary
    },
    text: {
      primary: COLOR.BLACK,
      secondary: COLOR.DARK_GREY,
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 14, // v4 default
    h1: { fontSize: '6rem' },
    h2: { fontSize: '3.75rem' },
    h3: { fontSize: '3rem' },
    h4: { fontSize: '2.125rem' },
    h5: { fontSize: '1.5rem' },
    h6: { fontSize: '1.25rem' },
    button: { textTransform: 'uppercase' },
  },
  shape: {
    borderRadius: 4, // v4 default
  },
  components: {
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          //  color: COLOR.BRAND.STD, // default for ALL icons
          // fontSize: '1.75rem', // closer to v4 default (28px instead of 24px)
        },
      },
      variants: [
        {
          props: { fontSize: "search" },
          style: {
            fontSize: 30,
            marginRight: 10,
            marginTop: "auto",
            marginBottom: "auto",
          }
        },
        // {
        //   props: { fontSize: "radio" },
        //   style: {
        //     fontSize: 30,
        //     marginRight: 10,
        //     marginTop: "auto",
        //     marginBottom: "auto",
        //     color: "purple",
        //   }
        // },
      ],
    },
    MuiAppBar: {
      defaultProps: {
        position: "static",
      },
      styleOverrides: {
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          height: "1px",
          border: 0,
          flexShrink: 0,
          backgroundColor: COLOR.LIGHT_GREY,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          // brand-tinted row hover, rather than MUI's default neutral grey overlay
          "&.MuiTableRow-hover:hover": {
            backgroundColor: alpha(COLOR.BRAND.STD, 0.08),
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "4px", // v4 button radius
        },
      },
      // variants - eg 'login' - must be included in ButtonPropsVariantOverrides (see theme.d.ts)
      variants: [
        {
          props: { variant: "login" },
          style: {
            marginRight: "15px",
            color: "white",
            fontSize: "12px",
            "&:hover": {
              color: "black",
              background: "white",
            },
          },
        },
        {
          props: { variant: "userMenu" },
          style: {
            ...buttonCommon,
            textTransform: "none",
            color: COLOR.NAVBAR_TEXT,
            backgroundColor: "transparent",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "transparent",
              boxShadow: "none",
            },
          },
        },
        {
          // hamburger menu
          props: { variant: "menu" },
          style: {
            ...buttonCommon,
            textTransform: "none",
            color: COLOR.NAVBAR_TEXT,
            marginRight: "7px",
            backgroundColor: "transparent",
          }
        },
        //#region 350 club
        {
          props: { variant: "promotionTextLandscape" },
          style: {
            minWidth: 350,
            cursor: "pointer",
            color: "black",
            backgroundColor: "white",
            marginLeft: 30,
            marginRight: 30,
            "&:hover": {
              color: "black",
              backgroundColor: "white",
              // opacity: "100%",
            },
          },
        },
        {
          props: { variant: "promotionTextPortrait" },
          style: {
            width: 200,
            cursor: "pointer",
            color: "black",
            backgroundColor: "white",
            marginLeft: 20,
            marginRight: 20,
            "&:hover": {
              color: "black",
              backgroundColor: "white",
              // opacity: "75%",
            },
          },
        }
        //#endregion
      ],
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          // Adjust icon button styles here
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          // default color for the icon
          "& .MuiSvgIcon-root": {
            color: COLOR.MID_GREY,
          },
          // when the radio is checked - brand colour, since these radios are used as
          // section/tab selectors rather than to indicate a "good" status
          "&.Mui-checked .MuiSvgIcon-root": {
            color: COLOR.BRAND.STD,
          },
        },
      },
      variants: [
        {
          props: { color: "success" }, // Example
          style: {
            "&.Mui-checked": {
              color: "green",
            },
          },
        },
      ],
    },
    MuiCheckbox: {
      defaultProps: {
        color: 'secondary', // v4 default color
      },
    },
    MuiSwitch: {
      defaultProps: {
        color: 'secondary', // v4 default color
      },
    },
    MuiToolbar: {
      defaultProps: {
        disableGutters: true,
      },
      styleOverrides: {
        root: {
          minHeight: `${KEY_VALUES.NAVBAR_HEIGHT}px`,
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true,
        TransitionProps: { timeout: 150 }, // v4 fade timing

        // Without this, MUI keeps a tooltip open while the pointer travels from the trigger
        // element onto the tooltip's own popper (so its contents can be selected/clicked) - if the
        // popper happens to sit under the cursor as it moves away (tabs, table column headers,
        // filter checkboxes etc. all have tooltips that can end up right where the pointer exits
        // to), the tooltip lingers instead of closing as soon as the pointer leaves the trigger.
        // None of this app's tooltips have interactive content, so there's nothing lost by making
        // them close on leaving the trigger itself.
        disableInteractive: true,

        // MUI's own default (100ms) reads as near-instant - a few call sites already set their own
        // longer enterDelay (KEY_VALUES.TOOLTIP_DELAY, 1000ms) via an explicit prop, which still
        // wins over this; everywhere else now waits half a second before showing a tooltip, so
        // simply passing over an element on the way to somewhere else doesn't pop one up.
        enterDelay: 500,
        // enterDelay only governs the *first* tooltip shown in a hover session - once one has
        // appeared, MUI treats the pointer as "already exploring" and shows the next one (e.g.
        // sliding from one tab to the next) after enterNextDelay instead, which defaults to 0. Left
        // alone, that made the delay above seem to only work the first time. Matching it here means
        // every element's tooltip waits the same ~0.5s, not just the first one hovered.
        enterNextDelay: 500,

        PopperProps: {
          modifiers: [
            {
              name: 'preventOverflow',
              options: {
                padding: 8, // ✅ always leave at least 8px from viewport edges
              },
            },
            {
              name: 'offset',
              options: {
                offset: [0, 4], // optional: add small vertical spacing
              },
            },
          ],
        },
      },
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgba(97, 97, 97, 0.9)',
          color: '#fff',
          fontSize: 18, // '0.75rem',
          fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
          fontWeight: 400,
          lineHeight: '1.4em',
          letterSpacing: '0.01071em',
          padding: '4px 8px',
          offset: 20,
          maxWidth: 300,
          margin: '2px 0',          // ✅ v4 had this, v5 removed it
          borderRadius: 4,          // ✅ v4 rounded corners
          wordWrap: 'break-word',   // ✅ v4 kept text inside
        },
        arrow: {
          color: 'rgba(97, 97, 97, 0.9)',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'standard', // v4 default variant
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: "black", // default for ALL Typography
        },
      },
      // defaultProps: {
      // variant: 'body1', // v4 default variant
      //  color: "green",
      // },
      variants: [
        {
          props: { variant: "userMenu" },
          style: {
            marginRight: "15px",
            fontSize: "16px",
            alignSelf: "right",
            lineHeight: 1,
            color: "red",//COLOR.NAVBAR_TEXT,
            cursor: "pointer",
          },
        },
        {
          // Title bar radio buttons text
          props: { variant: "radioButton" },
          style: {
            fontWeight: "bold",
            fontSize: "17px",
            // color: "green",
            lineHeight: 1.1,
            marginLeft: -5,
            paddingRight: 0,
            paddingTop: 3,
            paddingBottom: 1,
            marginTop: "auto",
            marginBottom: "auto",
            textAlign: "center",
          }
        },
        {
          // Title bar company/person name
          props: { variant: "titleText" },
          style: {
            fontWeight: "bold",
            fontSize: "20px",
            lineHeight: 1.1,
            // paddingLeft: 10,
            paddingRight: "10px",
            paddingTop: "1px",
            paddingBottom: "1px",
            marginTop: "auto",
            marginBottom: "auto",
            textAlign: "center",
          }
        },
        {
          // Navbar version
          props: { variant: "versionId" },
          style: {
            marginRight: "10px",
            fontSize: "12px",
            color: COLOR.NAVBAR_TEXT,
          },
        },
        {
          // Navbar monitoring and alerts
          props: { variant: "alertsConfig" },
          style: {
            cursor: "pointer",
            fontSize: "14px",
            marginRight: "20px",
            lineHeight: "1.1px",
            color: COLOR.NAVBAR_TEXT,
          },
        },
        {
          // Navbar "TRIAL: 14 days left"
          props: { variant: "trial" },
          style: {
            fontSize: "12px",
            marginLeft: "10px",
            lineHeight: "1.1px",
            color: COLOR.NAVBAR_TEXT,
          },
        },
        {
          //#region 350 club
          props: { variant: "descriptor" },
          style: {
            fontSize: 16,
            lineHeight: 1.1,
            color: COLOR.NAVBAR_TEXT,
            paddingLeft: 20,
            paddingBottom: 2,
            align: "left",
          },
        },
        //#endregion
      ],
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: {
          lineHeight: 1.5,   // closer to v4
          marginBottom: '1em', // if you want the old paragraph spacing
        },
      },
    },
  },
})

/*
const tooltipFontSize = "18px"

export const theme = () => createTheme({
  palette: {
    primary: {
      main: COLOR.BRAND.STD,
      contrastText: COLOR.WHITE,
    },
    secondary: {
      main: COLOR.GOOD,
    },
    error: {
      main: COLOR.WARN,
    },
    text: {
      primary: COLOR.BLACK,
      secondary: COLOR.DARK_GREY,
    },
    // awesome: {
    //   main: '#69ffa3ff',     // your primary color for "awesome"
    //   light: '#b6ffd4ff',
    //   dark: '#C71585',
    //   contrastText: '#fff'
    // },
    // card: {
    //   border: `${COLOR.WARN}`,
    //   rowValue: {
    //     main: '#69ffa3ff',     // your primary color for "awesome"
    //     light: '#ffeaf7ff',
    //     dark: '#C71585',
    //     contrastText: '#fff',
    //     // fontWeight: "bold",
    //     // minWidth: 125,
    //     // textAlign: "right",
    //     // color: green,
    //   },
    //   envelope: {
    //     display: CARD.ENVELOPE.DISPLAY,
    //     flexWrap: CARD.ENVELOPE.FLEXWRAP,
    //     maxWidth: CARD.ENVELOPE.MAXWIDTH,
    //     justifyContent: CARD.ENVELOPE.JUSTIFYCONTENT,
    //     margin: CARD.ENVELOPE.MARGIN, // centralises this div in card container
    //     padding: CARD.ENVELOPE.PADDING,
    //     // height: "auto",
    //     backgroundColor: "red",
    //   }
    // }
  },
  overrides: {
    MuiTooltip: {
      tooltip: {
        fontSize: tooltipFontSize,
      }
    },
  },

  // components: {
  //   MuiCard: {
  //     styleOverrides: {
  //       root: {
  //         // border: CARD.BORDER,
  //         border: portrait ? '10px solid #4fda4aff' : '10px solid #da4a4aff', // or use theme.palette.card.border here
  //       },
  //     },
  //     // MuiCardContainer: {
  //     // width: CARD.CONTAINER.WIDTH,
  //     // // height: "auto",
  //     // // overflowY: "scroll",
  //     // backgroundColor: CARD.CONTAINER.BACKGROUND_COLOR,
  //     // },
  //   },
  // }
})

.title {
  font-weight: "bold" !important;
  font-size: variables.$big-text !important;
  // background-color: colours.$numerable-light !important;
  // background-color: map-get(map-get(colours.$brand, numerable), light) !important;
  background-color: map-deep-get(colours.$brand, numerable, light) !important;
  // background-color: green !important;
}
*/